import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl-next';

import type { ExtensionManifest, ExtensionModule } from '@atlaskit/editor-common/extensions';
import Lozenge from '@atlaskit/lozenge';
import TemplateIcon from '@atlaskit/icon-lab/core/template';

import type { Snippet } from '../types/snippet.ts';
import {
	extractSnippetKeywords,
	createSnippetDescription,
	filterAndSort,
} from '../utils/quickInsertUtils.ts';

const messages = defineMessages({
	snippetsQuickInsertTitle: {
		id: 'snippets.snippetsQuickInsertTitle',
		defaultMessage: 'Block template',
		description: 'Title for the block template quick insert item',
	},
	newLozenge: {
		id: 'snippets.quickInsert.newLozenge',
		defaultMessage: 'New',
		description: 'Text in lozenge next to the Block Template quick insert item',
	},
	snippetIconLabel: {
		id: 'snippets.snippetIconLabel',
		defaultMessage: 'Block template icon',
		description: 'Label for the block template icon',
	},
	snippetQuickInsertDescription: {
		id: 'snippets.snippetQuickInsertDescription',
		defaultMessage: 'Add a reusable template to your page',
		description: 'Description for the block template quick insert item',
	},
});

type CreateSnippetExtensionProps = {
	snippets: Snippet[];
	intl: IntlShape;
	onOpenTemplatePanel?: () => void;
	userId?: string | null;
};

export const snippetExtensionManifest = ({
	snippets,
	intl,
	onOpenTemplatePanel,
	userId,
}: CreateSnippetExtensionProps): ExtensionManifest => {
	const snippetsQuickInsertTitle = intl.formatMessage(messages.snippetsQuickInsertTitle);
	const snippetIconLabel = intl.formatMessage(messages.snippetIconLabel);
	const snippetQuickInsertDescription = intl.formatMessage(messages.snippetQuickInsertDescription);
	const quickInsert: ExtensionModule[] = filterAndSort(snippets, userId).map((snippet) => ({
		key: `snippet-${snippet.snippetId}`,
		title: snippet.name,
		description: createSnippetDescription(snippet.createdBy, intl),
		keywords: extractSnippetKeywords(snippet.name, intl),
		icon: async () => () => <TemplateIcon label={snippetIconLabel} />,
		action: async () => snippet.body,
	}));
	// Add "Block Template" quick insert item if callback is provided
	if (onOpenTemplatePanel) {
		quickInsert.unshift({
			key: 'block-template',
			title: snippetsQuickInsertTitle,
			description: snippetQuickInsertDescription,
			keywords: extractSnippetKeywords(messages.snippetsQuickInsertTitle.defaultMessage, intl),
			icon: async () => () => <TemplateIcon label={snippetIconLabel} />,
			lozenge: <Lozenge appearance="new">{intl.formatMessage(messages.newLozenge)}</Lozenge>,
			action: async () => {
				onOpenTemplatePanel();
			},
		});
	}

	return {
		title: snippetsQuickInsertTitle,
		description: snippetQuickInsertDescription,
		type: 'com.atlassian.confluence.macro.core',
		key: 'snippet-extension',
		icons: {
			// eslint-disable-next-line import/dynamic-import-chunkname
			'48': () => import('@atlaskit/icon-lab/core/template'),
		},
		modules: {
			quickInsert,
		},
	};
};
